/**
 * Account Management Resource
 *
 * Thin 1:1 mapping to the account-management API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  Account,
  CreateAccountRequest,
  User,
  EnsureUserRequest,
  UpdateUserRequest,
  ListUsersParams,
  AccountMember,
  LinkUserToAccountRequest,
} from '../types/index.js';

export class AccountManagementResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/account-management';

  // --- Organizations ---

  /**
   * Create a real Stytch B2B organization and its default account. Does not
   * switch the caller's session into the new org (`auth` is not
   * agent-dispatchable). After this returns, the user must switch in the
   * account picker before further writes land there.
   */
  async createOrganization(
    params: CreateOrganizationRequest,
  ): Promise<Organization> {
    return this.http.post(`${this.prefix}/organizations`, params);
  }

  async listOrganizations(): Promise<Organization[]> {
    return this.http.get(`${this.prefix}/organizations`);
  }

  async getOrganization(organizationId: string): Promise<Organization> {
    return this.http.get(
      `${this.prefix}/organizations/${organizationId}`,
    );
  }

  async updateOrganization(
    organizationId: string,
    params: UpdateOrganizationRequest,
  ): Promise<Organization> {
    return this.http.patch(
      `${this.prefix}/organizations/${organizationId}`,
      params,
    );
  }

  // --- Accounts ---

  async createAccount(
    organizationId: string,
    params: CreateAccountRequest,
  ): Promise<Account> {
    return this.http.post(
      `${this.prefix}/organizations/${organizationId}/accounts`,
      params,
    );
  }

  async listAccounts(organizationId: string): Promise<Account[]> {
    return this.http.get(
      `${this.prefix}/organizations/${organizationId}/accounts`,
    );
  }

  async getAccount(
    organizationId: string,
    accountId: string,
  ): Promise<Account> {
    return this.http.get(
      `${this.prefix}/organizations/${organizationId}/accounts/${accountId}`,
    );
  }

  /**
   * Permanently erase a customer account from the product. The backend records
   * that as a soft-delete: calling on an already-deleted account also returns
   * successfully (both "live → soft-deleted" and "already soft-deleted" are
   * 204). Calling on a non-existent account id raises a 404. Search-pod
   * teardown and other downstream resource cleanup fire from the backend
   * workflow.
   */
  async deleteAccount(
    organizationId: string,
    accountId: string,
  ): Promise<void> {
    return this.http.delete(
      `${this.prefix}/organizations/${organizationId}/accounts/${accountId}`,
    );
  }

  // --- Users ---

  async ensureUser(params: EnsureUserRequest): Promise<User> {
    return this.http.post(`${this.prefix}/users/ensure`, params);
  }

  async listUsers(params?: ListUsersParams): Promise<User[]> {
    return this.http.get(`${this.prefix}/users`, params);
  }

  async getUser(userId: string): Promise<User> {
    return this.http.get(`${this.prefix}/users/${userId}`);
  }

  async updateUser(
    userId: string,
    params: UpdateUserRequest,
  ): Promise<User> {
    return this.http.patch(`${this.prefix}/users/${userId}`, params);
  }

  async deleteUser(userId: string): Promise<void> {
    return this.http.delete(`${this.prefix}/users/${userId}`);
  }

  async listUserAccounts(userId: string): Promise<Account[]> {
    return this.http.get(`${this.prefix}/users/${userId}/accounts`);
  }

  // --- Account Membership ---

  async linkUserToAccount(
    organizationId: string,
    accountId: string,
    params: LinkUserToAccountRequest,
  ): Promise<AccountMember> {
    return this.http.post(
      `${this.prefix}/organizations/${organizationId}/accounts/${accountId}/users`,
      params,
    );
  }

  async listAccountMembers(
    organizationId: string,
    accountId: string,
  ): Promise<AccountMember[]> {
    return this.http.get(
      `${this.prefix}/organizations/${organizationId}/accounts/${accountId}/users`,
    );
  }

  async unlinkUserFromAccount(
    organizationId: string,
    accountId: string,
    userId: string,
  ): Promise<void> {
    return this.http.delete(
      `${this.prefix}/organizations/${organizationId}/accounts/${accountId}/users/${userId}`,
    );
  }
}
